import { createBrowserRouter, RouterProvider, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import ProjectDashboard from './pages/ProjectDashboard/index';
import ProjectToolPage from './pages/ProjectDashboard/ProjectToolPage';
import RulesPage from './pages/RulesPage/index';
import SimulatorPage from './pages/SimulatorPage';
import DeployPage from './pages/DeployPage';
import FamiliesPage from './pages/FamiliesPage';
import AdminDashboard from './pages/AdminDashboard';
import ReleaseSchedule from './pages/AdminDashboard/ReleaseSchedule';
import VersionControlDashboard from './pages/VersionControlDashboard';
import OrganizationDashboard from './pages/OrganizationDashboard';
import UsageBilling from './pages/OrganizationDashboard/UsageBilling';
import PlaceholderPage from './pages/PlaceholderPage';
import { ChangelogPage } from '@product-changelog';

function LegacyFamiliesRedirect() {
    const { search, hash } = useLocation();
    return <Navigate to={{ pathname: '/products', search, hash }} replace />;
}

const router = createBrowserRouter([
    {
        path: "/",
        element: <Layout />,
        children: [
            {
                index: true,
                element: <Navigate to="/projects" replace />
            },
            {
                path: "rules",
                element: <RulesPage />
            },
            {
                path: "simulator",
                element: <SimulatorPage />
            },
            {
                path: "projects",
                element: <ProjectDashboard />
            },
            {
                path: "projects/logic",
                element: <ProjectToolPage toolKey="Logic" />
            },
            {
                path: "projects/deployment",
                element: <ProjectToolPage toolKey="Deployment" />
            },
            {
                path: "projects/variant",
                element: <ProjectToolPage toolKey="Variant" />
            },
            {
                path: "projects/pricing",
                element: <ProjectToolPage toolKey="Pricing" />
            },
            {
                path: "products",
                element: <FamiliesPage />
            },
            {
                path: "families",
                element: <LegacyFamiliesRedirect />
            },
            {
                path: "deploy",
                element: <DeployPage />
            },
            {
                path: "deploy/data",
                element: <DeployPage />
            },
            {
                path: "deploy/designer",
                element: <DeployPage />
            },
            {
                path: "deploy/integration",
                element: <DeployPage />
            },
            {
                path: "deploy/analytics",
                element: <DeployPage />
            },
            {
                path: "version-control",
                element: <VersionControlDashboard />
            },

            // Admin Routes
            {
                path: "admin/relationships",
                element: <AdminDashboard tab="relationships" />
            },
            {
                path: "admin/users",
                element: <AdminDashboard tab="users" />
            },
            {
                path: "admin/organizations",
                element: <AdminDashboard tab="organizations" />
            },
            {
                path: "admin/release",
                element: <ReleaseSchedule />
            },

            {
                path: "organization/members",
                element: <OrganizationDashboard tab="members" />
            },
            {
                path: "organization/invitations",
                element: <OrganizationDashboard tab="invitations" />
            },
            {
                path: "organization/settings",
                element: <OrganizationDashboard tab="settings" />
            },
            {
                path: "organization/usage",
                element: <UsageBilling />
            },

            // Variant Routes
            {
                path: "variant/builder",
                element: <PlaceholderPage
                    title="Variant Builder"
                    tool="Variant"
                    icon="fa-solid fa-cubes"
                    description="Configure and resolve dynamic variant combinations and specifications."
                    mockStats={[
                        { label: 'Total Variations', value: '1,280', change: '+12% this week', changeType: 'positive' },
                        { label: 'Configured Rulesets', value: '14 Active', change: 'All checks passed', changeType: 'info' },
                        { label: 'System Validation', value: '100% Healthy', change: 'Last run: 5m ago', changeType: 'positive' }
                    ]}
                    mockData={[
                        { sku: 'VRT-CAR-V6-LXI', name: 'Premium Sedan V6 Configuration', baseProduct: 'Sedan_Car', status: true },
                        { sku: 'VRT-TRK-V8-SLT', name: 'Heavy Duty Truck V8 Engine', baseProduct: 'Truck_Exhaust', status: true },
                        { sku: 'VRT-EV-HYB-ECO', name: 'Hybrid Electric Economy Build', baseProduct: 'Eco_Vehicle', status: false }
                    ]}
                />
            },
            {
                path: "variant/configurations",
                element: <PlaceholderPage
                    title="Configurations"
                    tool="Variant"
                    icon="fa-solid fa-sliders"
                    description="Define reference models, parameters, and variants mapping."
                    mockStats={[
                        { label: 'Active Reference Models', value: '8 Models', change: 'Stable', changeType: 'info' },
                        { label: 'Mapped Features', value: '112 Global/Local', change: '+3 new features', changeType: 'positive' },
                        { label: 'Draft Schemes', value: '2 in edit', changeType: 'info' }
                    ]}
                    mockData={[
                        { name: 'Sedan Variant Matrix', product: 'Sedan_Car', featuresCount: 18, status: true },
                        { name: 'Truck Variant Matrix', product: 'Truck_Exhaust', featuresCount: 24, status: true },
                        { name: 'SUV Comfort Matrix', product: 'SUV_Global', featuresCount: 15, status: false }
                    ]}
                />
            },
            {
                path: "variant/bulk",
                element: <PlaceholderPage
                    title="Bulk Manager"
                    tool="Variant"
                    icon="fa-solid fa-file-import"
                    description="Perform bulk ingestion, exports, and schema-wide updates."
                    mockStats={[
                        { label: 'Last Export File', value: 'variant_export_0604.csv', change: 'Size: 1.2 MB', changeType: 'info' },
                        { label: 'Queued Actions', value: '0 Tasks', change: 'Idle', changeType: 'info' },
                        { label: 'Sync Pipeline', value: 'Online', change: 'Connected to Variant', changeType: 'positive' }
                    ]}
                    mockData={[
                        { jobId: 'JOB-982', type: 'Export CSV', scope: 'All Variants', date: '2026-06-04 10:45', status: true },
                        { jobId: 'JOB-981', type: 'Bulk Update', scope: 'Sedan Products', date: '2026-06-03 14:12', status: true }
                    ]}
                />
            },

            // Pricing Routes
            {
                path: "pricing/books",
                element: <PlaceholderPage
                    title="Price Books"
                    tool="Pricing"
                    icon="fa-solid fa-book-open"
                    description="Manage catalog-wide pricing matrices and book rules."
                    mockStats={[
                        { label: 'Total Books', value: '4 Books', change: 'USD, EUR, GBP, CAD', changeType: 'info' },
                        { label: 'Active Items', value: '4,520 Priced SKUs', change: 'Synced with catalog', changeType: 'positive' },
                        { label: 'Base Book Currency', value: 'USD ($)', change: 'Reference rate active', changeType: 'info' }
                    ]}
                    mockData={[
                        { bookName: 'Global Retail Book 2026', baseCurrency: 'USD', status: true },
                        { bookName: 'EMEA Corporate Book', baseCurrency: 'EUR', status: true },
                        { bookName: 'APAC Distributor Matrix', baseCurrency: 'USD', status: false }
                    ]}
                />
            },
            {
                path: "pricing/rates",
                element: <PlaceholderPage
                    title="Rate Cards"
                    tool="Pricing"
                    icon="fa-solid fa-address-card"
                    description="Define contract-specific rates and partner margin cards."
                    mockStats={[
                        { label: 'Active Cards', value: '12 Partner Cards', change: '+1 custom deal added', changeType: 'positive' },
                        { label: 'Global Margin Override', value: '+4.5%', change: 'Applied automatically', changeType: 'info' },
                        { label: 'Audit Log', value: 'Clean', change: 'Checked 1h ago', changeType: 'positive' }
                    ]}
                    mockData={[
                        { cardName: 'Enterprise Gold Rate', partnerScope: 'Tier-1 Dist.', rateMultiplier: '0.90 (10% Off)', status: true },
                        { cardName: 'SaaS Subcontractor Special', partnerScope: 'External Eng.', rateMultiplier: '1.05 (5% Markup)', status: true }
                    ]}
                />
            },
            {
                path: "pricing/discounts",
                element: <PlaceholderPage
                    title="Discount Matrices"
                    tool="Pricing"
                    icon="fa-solid fa-percent"
                    description="Create rule-based discount triggers and matrix overlays."
                    mockStats={[
                        { label: 'Active Rule Rows', value: '38 Rules', change: 'Evaluation priority defined', changeType: 'info' },
                        { label: 'Max Discount Allowed', value: '35.0%', change: 'Strict ceiling active', changeType: 'info' },
                        { label: 'Conflict Status', value: '0 Conflicts', change: 'All matrices resolved', changeType: 'positive' }
                    ]}
                    mockData={[
                        { ruleSet: 'Bulk Volume Discounts', triggerVariable: 'Order.Quantity', discountPercent: 'Up to 25%', status: true },
                        { ruleSet: 'Ecosystem Bundle Discount', triggerVariable: 'Product.Bundle', discountPercent: 'Flat 10%', status: true }
                    ]}
                />
            },

            {
                path: "changelog",
                element: <ChangelogPage />
            },
            {
                path: "*",
                element: <Navigate to="/" replace />
            }
        ]
    }
]);

export default function App() {
    return (
        <RouterProvider router={router} />
    );
}
